import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchAdminChildren, fetchAdminParents, fetchAdminReports } from "@/admin/api";
import type { AdminChild, AdminParent, AdminReport } from "@/admin/types";
import AdminListCard from "@/pages/admin/components/AdminListCard";

export default function AdminCrm() {
  const { t, i18n } = useTranslation("common");
  const isRTL = i18n.language === "ar";

  const [parents, setParents] = useState<AdminParent[]>([]);
  const [children, setChildren] = useState<AdminChild[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<"parents" | "children" | "reports">("parents");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([
      fetchAdminParents().catch(() => []),
      fetchAdminChildren().catch(() => []),
      fetchAdminReports().catch(() => []),
    ])
      .then(([parentsData, childrenData, reportsData]) => {
        if (!isMounted) return;
        setParents(parentsData);
        setChildren(childrenData);
        setReports(reportsData);
      })
      .catch((err) => {
        console.error("Failed to fetch CRM data:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedParent = useMemo(() => {
    if (!selectedParentId) return null;
    return parents.find((p) => p.id === selectedParentId);
  }, [parents, selectedParentId]);

  // Filtered lists based on search query and selected parent
  const filteredParents = useMemo(() => {
    let list = parents;
    if (selectedParentId) {
      list = list.filter((p) => p.id === selectedParentId);
    }
    if (!searchQuery.trim()) return list;
    const query = searchQuery.toLowerCase();
    return list.filter(
      (p) =>
        p.name?.toLowerCase().includes(query) ||
        p.phoneNumber.includes(query) ||
        p.email?.toLowerCase().includes(query)
    );
  }, [parents, searchQuery, selectedParentId]);

  const filteredChildren = useMemo(() => {
    let list = children;
    if (selectedParentId) {
      list = list.filter((c) => c.parentId === selectedParentId);
    }
    if (!searchQuery.trim()) return list;
    const query = searchQuery.toLowerCase();
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.parentPhone.includes(query) ||
        c.gender.toLowerCase().includes(query)
    );
  }, [children, searchQuery, selectedParentId]);

  const filteredReports = useMemo(() => {
    let list = reports;
    if (selectedParentId) {
      list = list.filter((r) => r.child.parentId === selectedParentId);
    }
    if (!searchQuery.trim()) return list;
    const query = searchQuery.toLowerCase();
    return list.filter(
      (r) =>
        r.child.name.toLowerCase().includes(query) ||
        r.child.parentPhone.includes(query) ||
        r.id.toLowerCase().includes(query)
    );
  }, [reports, searchQuery, selectedParentId]);

  const calculateAge = (dob: string): string => {
    if (!dob) return "";
    const birth = new Date(dob);
    const now = new Date();
    const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years > 0) {
      return isRTL
        ? `${years} ${years === 1 ? "سنة" : "سنوات"} ${remainingMonths > 0 ? `و ${remainingMonths} أشهر` : ""}`
        : `${years} ${years === 1 ? "year" : "years"} ${remainingMonths > 0 ? `and ${remainingMonths} months` : ""}`;
    }
    return isRTL
      ? `${months} ${months === 1 ? "شهر" : "أشهر"}`
      : `${months} ${months === 1 ? "month" : "months"}`;
  };

  const getStatusBadge = (scorePhysical: number, scoreCognitive: number, scoreSocial: number) => {
    const score = Math.round((scorePhysical + scoreCognitive + scoreSocial) / 3);
    if (score >= 80) {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-600">
          {score}% · {t("excellent")}
        </span>
      );
    }
    if (score >= 60) {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-yellow-50 text-yellow-600">
          {score}% · {t("good")}
        </span>
      );
    }
    if (score >= 40) {
      return (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-50 text-orange-600">
          {score}% · {t("developing")}
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-600">
        {score}% · {t("needsAttention")}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-400 text-sm">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-start" dir={isRTL ? "rtl" : "ltr"}>
      {/* Page Title */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-navy-400">{t("adminCrm")}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isRTL ? "إدارة ومتابعة سجلات أولياء الأمور، الأطفال، وتقارير التطور الصادرة" : "Manage and track parent accounts, children, and developmental reports"}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-400/10 text-primary-400 flex items-center justify-center text-xl">
            <i className="ri-parent-line" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">{t("adminTotalParents")}</p>
            <h3 className="text-xl md:text-2xl font-bold text-navy-400 mt-1">{parents.length}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center text-xl">
            <i className="ri-user-smile-line" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">{t("adminTotalChildren")}</p>
            <h3 className="text-xl md:text-2xl font-bold text-navy-400 mt-1">{children.length}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center text-xl">
            <i className="ri-file-chart-line" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">{t("adminTotalReports")}</p>
            <h3 className="text-xl md:text-2xl font-bold text-navy-400 mt-1">{reports.length}</h3>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Navigation Tabs and Search */}
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Tabs */}
          <div className="flex bg-gray-100/80 p-1 rounded-xl">
            <button
              onClick={() => {
                setActiveTab("parents");
                setSearchQuery("");
              }}
              className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-lg transition-all ${
                activeTab === "parents" ? "bg-white text-navy-400 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span>{t("adminParents")}</span>
              <span className="ml-1.5 bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
                {parents.length}
              </span>
            </button>
            <button
              onClick={() => {
                setActiveTab("children");
                setSearchQuery("");
              }}
              className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-lg transition-all ${
                activeTab === "children" ? "bg-white text-navy-400 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span>{t("adminChildren")}</span>
              <span className="ml-1.5 bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
                {children.length}
              </span>
            </button>
            <button
              onClick={() => {
                setActiveTab("reports");
                setSearchQuery("");
              }}
              className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-lg transition-all ${
                activeTab === "reports" ? "bg-white text-navy-400 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span>{t("adminReports")}</span>
              <span className="ml-1.5 bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
                {reports.length}
              </span>
            </button>
          </div>

          {/* Search bar */}
          <div className="relative w-full md:w-72">
            <i className={`ri-search-line absolute top-1/2 -translate-y-1/2 ${isRTL ? "right-3" : "left-3"} text-gray-400`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                activeTab === "parents"
                  ? isRTL
                    ? "ابحث بالاسم، الجوال، البريد..."
                    : "Search by name, phone, email..."
                  : activeTab === "children"
                  ? isRTL
                    ? "ابحث باسم الطفل، الجوال..."
                    : "Search by child name, parent phone..."
                  : isRTL
                  ? "ابحث باسم الطفل، الجوال، المعرف..."
                  : "Search by child name, parent phone, ID..."
              }
              className={`w-full ${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 outline-none text-xs md:text-sm transition-all text-start`}
            />
          </div>
        </div>

        {/* Selected Parent Filter Banner */}
        {selectedParent && (
          <div className="px-5 py-3 bg-primary-400/10 border-b border-gray-100 flex items-center justify-between text-xs md:text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-filter-2-line text-primary-500 text-base" />
              <span className="text-primary-500 font-semibold">
                {isRTL
                  ? `عرض أطفال وتقارير ولي الأمر: ${selectedParent.name || selectedParent.phoneNumber}`
                  : `Showing children and reports for parent: ${selectedParent.name || selectedParent.phoneNumber}`}
              </span>
            </div>
            <button
              onClick={() => setSelectedParentId(null)}
              className="text-gray-400 hover:text-red-500 flex items-center gap-1 font-bold transition-colors"
            >
              <i className="ri-close-circle-line" />
              <span>{isRTL ? "إلغاء التصفية" : "Clear filter"}</span>
            </button>
          </div>
        )}

        {/* Desktop Content Lists */}
        <div className="hidden md:block overflow-x-auto">
          {/* PARENTS TABLE */}
          {activeTab === "parents" && (
            <table className="w-full text-sm text-start">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs font-bold border-b border-gray-100">
                  <th className="px-6 py-4 text-start">{isRTL ? "اسم ولي الأمر" : "Parent Name"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "رقم الجوال" : "Phone Number"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "البريد الإلكتروني" : "Email Address"}</th>
                  <th className="px-6 py-4 text-center">{isRTL ? "عدد الأطفال" : "Children Count"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "تاريخ الانضمام" : "Joined Date"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredParents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                      {t("adminNoResults")}
                    </td>
                  </tr>
                ) : (
                  filteredParents.map((parent) => (
                    <tr key={parent.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-navy-400">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-primary-400/10 text-primary-500 flex items-center justify-center text-xs font-bold">
                            {(parent.name || parent.phoneNumber).slice(0, 2)}
                          </div>
                          <span>{parent.name || (isRTL ? "غير محدد" : "N/A")}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 font-mono" dir="ltr">{parent.phoneNumber}</td>
                      <td className="px-6 py-4 text-gray-500">{parent.email || (isRTL ? "غير متوفر" : "N/A")}</td>
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedParentId(parent.id);
                            setActiveTab("children");
                          }}
                          className="px-2.5 py-0.5 rounded-full bg-primary-400/10 hover:bg-primary-400/20 text-primary-500 text-xs font-bold transition-colors cursor-pointer"
                          title={isRTL ? "عرض أطفال ولي الأمر هذا" : "View children of this parent"}
                        >
                          {parent.childrenCount}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-xs">
                        {new Date(parent.createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* CHILDREN TABLE */}
          {activeTab === "children" && (
            <table className="w-full text-sm text-start">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs font-bold border-b border-gray-100">
                  <th className="px-6 py-4 text-start">{isRTL ? "اسم الطفل" : "Child Name"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "الجنس" : "Gender"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "العمر" : "Age"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "جوال ولي الأمر" : "Parent Phone"}</th>
                  <th className="px-6 py-4 text-center">{isRTL ? "عدد التقارير" : "Reports Count"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredChildren.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                      {t("adminNoResults")}
                    </td>
                  </tr>
                ) : (
                  filteredChildren.map((child) => (
                    <tr key={child.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-navy-400">
                        <div className="flex items-center gap-2">
                          <i className={child.gender === "male" ? "ri-men-line text-blue-500" : "ri-women-line text-pink-500"} />
                          <span>{child.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          child.gender === "male" ? "bg-blue-50 text-blue-600" : "bg-pink-50 text-pink-600"
                        }`}>
                          {child.gender === "male" ? t("male") : t("female")}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{calculateAge(child.dob)}</td>
                      <td className="px-6 py-4 text-gray-500 font-mono" dir="ltr">{child.parentPhone}</td>
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedParentId(child.parentId);
                            setActiveTab("reports");
                          }}
                          className="px-2.5 py-0.5 rounded-full bg-gray-100 hover:bg-primary-400/20 hover:text-primary-500 text-gray-600 text-xs font-bold transition-colors cursor-pointer"
                          title={isRTL ? "عرض تقارير عائلة هذا الطفل" : "View reports for this child's family"}
                        >
                          {child.reportsCount}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* REPORTS TABLE */}
          {activeTab === "reports" && (
            <table className="w-full text-sm text-start">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs font-bold border-b border-gray-100">
                  <th className="px-6 py-4 text-start">{isRTL ? "اسم الطفل" : "Child Name"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "جوال ولي الأمر" : "Parent Phone"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "حالة النمو العام" : "Overall Growth"}</th>
                  <th className="px-6 py-4 text-start">{isRTL ? "تاريخ الإصدار" : "Issued Date"}</th>
                  <th className="px-6 py-4 text-center">{isRTL ? "الإجراءات" : "Actions"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                      {t("adminNoResults")}
                    </td>
                  </tr>
                ) : (
                  filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-navy-400">{report.child.name}</td>
                      <td className="px-6 py-4 text-gray-500 font-mono" dir="ltr">{report.child.parentPhone}</td>
                      <td className="px-6 py-4">
                        {getStatusBadge(report.scorePhysical, report.scoreCognitive, report.scoreSocial)}
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-xs">
                        {new Date(report.createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <a
                          href={`/report/${report.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-primary-500 bg-primary-400/10 hover:bg-primary-400/20 rounded-lg transition-colors"
                        >
                          <i className="ri-external-link-line" />
                          <span>{isRTL ? "عرض التقرير" : "View"}</span>
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile Card Views */}
        <div className="block md:hidden p-4 space-y-4">
          {/* PARENTS MOBILE CARDS */}
          {activeTab === "parents" && (
            <>
              {filteredParents.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">{t("adminNoResults")}</div>
              ) : (
                filteredParents.map((parent) => (
                  <AdminListCard
                    key={parent.id}
                    title={parent.name || (isRTL ? "غير محدد" : "N/A")}
                    iconFallback="ri-parent-line"
                    badges={[
                      { label: parent.phoneNumber, icon: "ri-phone-line" },
                      { label: parent.email || (isRTL ? "غير متوفر" : "N/A"), icon: "ri-mail-line" },
                      { label: `${parent.childrenCount} ${isRTL ? "أطفال" : "children"}`, icon: "ri-user-smile-line" },
                    ]}
                    customActions={
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedParentId(parent.id);
                          setActiveTab("children");
                        }}
                        className="px-3 py-2 rounded-xl bg-primary-400/10 hover:bg-primary-400/20 text-primary-500 text-xs font-bold transition-colors"
                      >
                        <i className="ri-arrow-left-line text-sm" />
                      </button>
                    }
                    footerLeft={
                      <span className="text-[10px] text-gray-400">
                        {new Date(parent.createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    }
                  />
                ))
              )}
            </>
          )}

          {/* CHILDREN MOBILE CARDS */}
          {activeTab === "children" && (
            <>
              {filteredChildren.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">{t("adminNoResults")}</div>
              ) : (
                filteredChildren.map((child) => (
                  <AdminListCard
                    key={child.id}
                    title={child.name}
                    iconFallback={child.gender === "male" ? "ri-men-line" : "ri-women-line"}
                    badges={[
                      {
                        label: child.gender === "male" ? t("male") : t("female"),
                        icon: child.gender === "male" ? "ri-men-line" : "ri-women-line",
                      },
                      { label: calculateAge(child.dob), icon: "ri-calendar-todo-line" },
                      { label: child.parentPhone, icon: "ri-phone-line" },
                      { label: `${child.reportsCount} ${isRTL ? "تقارير" : "reports"}`, icon: "ri-file-chart-line" },
                    ]}
                    customActions={
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedParentId(child.parentId);
                          setActiveTab("reports");
                        }}
                        className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-primary-400/20 hover:text-primary-500 text-gray-600 text-xs font-bold transition-colors"
                      >
                        <i className="ri-file-chart-line text-sm" />
                      </button>
                    }
                  />
                ))
              )}
            </>
          )}

          {/* REPORTS MOBILE CARDS */}
          {activeTab === "reports" && (
            <>
              {filteredReports.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">{t("adminNoResults")}</div>
              ) : (
                filteredReports.map((report) => (
                  <AdminListCard
                    key={report.id}
                    title={report.child.name}
                    iconFallback="ri-file-chart-line"
                    badges={[
                      { label: report.child.parentPhone, icon: "ri-phone-line" },
                      {
                        label: new Date(report.createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }),
                        icon: "ri-calendar-line",
                      },
                    ]}
                    footerLeft={
                      getStatusBadge(report.scorePhysical, report.scoreCognitive, report.scoreSocial)
                    }
                    customActions={
                      <a
                        href={`/report/${report.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-primary-500 bg-primary-400/10 hover:bg-primary-400/20 rounded-xl transition-colors"
                      >
                        <i className="ri-external-link-line" />
                        <span>{isRTL ? "عرض" : "View"}</span>
                      </a>
                    }
                  />
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
